import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import apiPath from "../../Utils/apiPath";
import { apiGet } from "../../Utils/apiFetch";
import { debounce, isEmpty, upperCase } from "lodash";
import { toFixedWithoutRounding } from "../../Utils/Toast";
import CalendarWeb from "./CalendarWeb";
import CustomSelect from "./CustomSelect";
import UnavailableFeatureDialog from "../../component/UnavailableFeatureDialog";

const TABS = ["Slot", "Live", "Fishing", "Poker", "Sport"];
const FORMAT = "YYYY-MM-DD HH:mm:ss";
const SPORT_VENDORS = ["All", "BetFair", "Bookmaker", "Fancy", "SportsBook"];
const CASINO_VENDORS = ["All"];

const HEADERS = [
  "Bet time",
  "Bet amount",
  "Valid bet",
  "Award",
  "Profit and loss",
  "Game name",
  "Game number",
];

const BET_TYPE_MAP = {
  BetFair: "betfair",
  Bookmaker: "bookmaker",
  Fancy: "fancy",
  SportsBook: "sportBook",
};

const EVENT_TYPE = {
  4: "Cricket",
  2: "Tennis",
  1: "Soccer",
  3: "Casino",
};

const money = (value, decimals) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return decimals ? "0.00" : "0";
  if (!decimals) return String(Math.round(num));
  return toFixedWithoutRounding(num);
};

export default function BettingRecords() {
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showNumberSearch, setShowNumberSearch] = useState(false);
  const [numberSearch, setNumberSearch] = useState("");
  const [appliedNumberSearch, setAppliedNumberSearch] = useState("");
  const [decimals, setDecimals] = useState(false);
  const [unavailable, setUnavailable] = useState("");
  const [totals, setTotals] = useState({
    betAmount: 0,
    validBet: 0,
    award: 0,
    profitLoss: 0,
  });
  const [filter, setFilter] = useState({
    betType: "All",
    fromPeriod: moment().startOf("day").format(FORMAT),
    toPeriod: moment().endOf("day").format(FORMAT),
    page: 1,
    limit: 50,
    tab: "Slot",
  });

  const from = moment(filter.fromPeriod);
  const to = moment(filter.toPeriod);
  const isToday = from.isSame(moment(), "day") && to.isSame(moment(), "day");
  const isYesterday =
    from.isSame(moment().subtract(1, "day"), "day") &&
    to.isSame(moment().subtract(1, "day"), "day");
  const isLast7Days =
    from.isSame(moment().subtract(6, "days"), "day") && to.isSame(moment(), "day");
  const isSport = filter.tab === "Sport";

  const setRange = (preset) => {
    if (preset === "today") {
      return {
        fromPeriod: moment().startOf("day").format(FORMAT),
        toPeriod: moment().endOf("day").format(FORMAT),
      };
    }
    if (preset === "yesterday") {
      return {
        fromPeriod: moment().subtract(1, "day").startOf("day").format(FORMAT),
        toPeriod: moment().subtract(1, "day").endOf("day").format(FORMAT),
      };
    }
    return {
      fromPeriod: moment().subtract(6, "days").startOf("day").format(FORMAT),
      toPeriod: moment().endOf("day").format(FORMAT),
    };
  };

  const showPrice = (res) => {
    if (isEmpty(res?.fancyName)) {
      if (
        (res?.teamSelectionWin == res?.selectionId && res?.betType === "back") ||
        (res?.teamSelectionWin != res?.selectionId && res?.betType === "lay")
      ) {
        return Number(res?.profitAmount || 0);
      }
      return -Number(res?.loseAmount || 0);
    }
    if (res?.type === "No") {
      return res?.decisionRun < res?.betRun
        ? Number(res?.profitAmount || 0)
        : -Number(res?.loseAmount || 0);
    }
    return res?.decisionRun >= res?.betRun
      ? Number(res?.profitAmount || 0)
      : -Number(res?.loseAmount || 0);
  };

  const computeTotals = (rows, sport) => {
    return rows.reduce(
      (acc, res) => {
        const betAmount = Number(sport ? res?.amount : res?.betAmount || res?.amount || 0);
        const pl = sport ? showPrice(res) : Number(res?.playerPL || 0);
        const award = sport ? Math.max(pl, 0) : Number(res?.winAmount || res?.award || Math.max(pl, 0));
        const validBet = Number(res?.validBet || res?.validAmount || betAmount);
        acc.betAmount += betAmount;
        acc.validBet += validBet;
        acc.award += award;
        acc.profitLoss += pl;
        return acc;
      },
      { betAmount: 0, validBet: 0, award: 0, profitLoss: 0 }
    );
  };

  const getData = async (obj = filter) => {
    const sport = obj.tab === "Sport";
    const betType =
      sport && obj.betType !== "All" ? BET_TYPE_MAP[obj.betType] || "betfair" : sport ? "betfair" : "casino";

    const { status, data: response } = await apiGet(apiPath.getHistory, {
      betType,
      status: "completed",
      fromPeriod: moment(obj.fromPeriod).format("YYYY-MM-DD"),
      toPeriod: moment(obj.toPeriod).format("YYYY-MM-DD"),
      page: obj.page || 1,
      limit: obj.limit,
    });

    if (status === 200 && response?.success) {
      const rows = sport
        ? response?.results?.data || []
        : response?.results?.length
          ? response.results
          : response?.results?.data || [];
      setData(rows);
      setTotals(computeTotals(rows, sport));
    } else {
      setData([]);
      setTotals({ betAmount: 0, validBet: 0, award: 0, profitLoss: 0 });
    }
    setLoader(false);
  };

  const debouncedFetch = useCallback(
    debounce((obj) => {
      getData(obj);
    }, 400),
    []
  );

  useEffect(() => {
    setLoader(true);
    debouncedFetch(filter);
  }, [filter]);

  const vendorOptions = isSport ? SPORT_VENDORS : CASINO_VENDORS;

  const visibleRows = useMemo(() => {
    const needle = filter.betType.toLowerCase();
    return data.filter((row) => {
      const name = String(row?.platform || row?.casinoType || row?.provider || "").toLowerCase();
      const gameNumber = String(row?.matchBetId || row?.casinoBetId || row?.gameId || row?.clientName || "");
      return (isSport || filter.betType === "All" || name.includes(needle)) &&
        (!appliedNumberSearch || gameNumber.endsWith(appliedNumberSearch));
    });
  }, [data, filter.betType, isSport, appliedNumberSearch]);

  const applyPreset = (preset) => {
    setFilter((prev) => ({ ...prev, ...setRange(preset), page: 1 }));
  };

  const renderRow = (res, index) => {
    const betAmount = Number(isSport ? res?.amount : res?.betAmount || res?.amount || 0);
    const pl = isSport ? showPrice(res) : Number(res?.playerPL || 0);
    const award = isSport ? Math.max(pl, 0) : Number(res?.winAmount || res?.award || Math.max(pl, 0));
    const validBet = Number(res?.validBet || res?.validAmount || betAmount);
    const gameName = isSport
      ? [res?.matchName, EVENT_TYPE[res?.eventType], res?.teamName || res?.fancyName]
          .filter(Boolean)
          .join(" | ")
      : [res?.casinoName || res?.gameName, upperCase(res?.casinoType || filter.tab), res?.platform]
          .filter(Boolean)
          .join(" | ");
    const gameNumber = res?.matchBetId || res?.casinoBetId || res?.gameId || res?.clientName || "—";

    const cells = [
      moment(res?.timeInserted || res?.createdAt).format("YYYY-MM-DD HH:mm:ss"),
      money(betAmount, decimals),
      money(validBet, decimals),
      money(award, decimals),
      money(pl, decimals),
      gameName || "—",
      gameNumber,
    ];
    return (
      <div className="reference-betting-record-tr" key={res?._id || gameNumber || index}>
        {cells.map((value, cell) => (
          <div key={HEADERS[cell]} data-label={HEADERS[cell]} className={cell === 4 ? (pl >= 0 ? "is-credit" : "is-debit") : undefined}>{value}</div>
        ))}
      </div>
    );
  };

  return (
    <div className="reference-betting-record" id="mc_container">
      <div className="reference-betting-record-top">
        <div className="reference-betting-record-tabs">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={filter.tab === tab ? "active" : undefined}
              onClick={() => {
                setData([]);
                setTotals({ betAmount: 0, validBet: 0, award: 0, profitLoss: 0 });
                setFilter((prev) => ({
                  ...prev,
                  tab,
                  betType: "All",
                  page: 1,
                }));
              }}
            >
              {tab}
            </button>
          ))}
        </div>
        <button type="button" className="reference-betting-record-exclude" onClick={() => setUnavailable("Exclusion turnover list")}>
          Exclusion turnover list
        </button>
      </div>

      <div className="reference-betting-record-filters">
        <label className={isToday ? "active" : undefined}>
          <input type="radio" name="betting-record-date" checked={isToday} onChange={() => applyPreset("today")} />
          <span>Today</span>
        </label>
        <label className={isYesterday ? "active" : undefined}>
          <input type="radio" name="betting-record-date" checked={isYesterday} onChange={() => applyPreset("yesterday")} />
          <span>Yesterday</span>
        </label>
        <label className={isLast7Days ? "active" : undefined}>
          <input type="radio" name="betting-record-date" checked={isLast7Days} onChange={() => applyPreset("7days")} />
          <span>7-Days</span>
        </label>

        <button type="button" className="reference-betting-record-date" onClick={() => setShowCalendar(true)}>
          {`${moment(filter.fromPeriod).format("MM/DD")} ~ ${moment(filter.toPeriod).format("MM/DD")}`}
        </button>

        <div className="reference-betting-record-vendor">
          <span>Vendor</span>
          <CustomSelect
            value={filter.betType}
            option={vendorOptions}
            onChange={(betType) => setFilter((prev) => ({ ...prev, betType, page: 1 }))}
          />
        </div>

        <button
          type="button"
          className="reference-betting-record-search"
          disabled={showNumberSearch}
          onClick={() => {
            setLoader(true);
            getData(filter);
          }}
        >
          Search
        </button>
        <button
          type="button"
          className="reference-betting-record-gear"
          aria-label="Settings"
          aria-expanded={showNumberSearch}
          aria-controls="betting-number-search"
          onClick={() => {
            setShowNumberSearch((value) => !value);
            setAppliedNumberSearch("");
          }}
        >
          ⚙
        </button>
      </div>

      {showNumberSearch && <form id="betting-number-search" className="reference-betting-number-search" onSubmit={(event) => {
        event.preventDefault();
        setAppliedNumberSearch(numberSearch.trim());
      }}>
        <input aria-label="Search by Last Digits" placeholder="Search by Last Digits" value={numberSearch} onChange={(event) => setNumberSearch(event.target.value)} />
        <button type="submit">Search</button>
      </form>}

      {showCalendar ? (
        <CalendarWeb
          fromPeriod={filter.fromPeriod}
          toPeriod={filter.toPeriod}
          setFilter={setFilter}
          onCancel={() => setShowCalendar(false)}
        />
      ) : null}

      <div className="reference-betting-record-table">
        <div className="reference-betting-record-head">
          {HEADERS.map((label) => (
            <div key={label}>{label}</div>
          ))}
        </div>
        <div className="reference-betting-record-body">
          {loader ? <div className="reference-betting-record-loading" /> : null}
          {!loader && visibleRows.length === 0 ? (
            <div className="reference-betting-record-empty">No matched data found</div>
          ) : (
            visibleRows.map(renderRow)
          )}
        </div>
        <div className="reference-betting-record-foot">
          {["Total", totals.betAmount === 0 ? "0.00" : money(totals.betAmount, decimals), totals.validBet === 0 ? "0.00" : money(totals.validBet, decimals), totals.award === 0 ? "0.00" : money(totals.award, decimals), totals.profitLoss === 0 ? "0.00" : money(totals.profitLoss, decimals), "", ""].map((value, cell) => (
            <div key={HEADERS[cell] || `blank-${cell}`} data-label={cell === 0 ? "Summary" : cell > 4 ? "" : HEADERS[cell]} className={cell === 4 ? (totals.profitLoss >= 0 ? "is-credit" : "is-debit") : undefined}>{value}</div>
          ))}
        </div>
      </div>

      <div className="reference-betting-record-decimal">
        <span>Decimal point</span>
        <button
          type="button"
          className={decimals ? "on" : undefined}
          aria-label="Decimal point"
          aria-pressed={decimals}
          onClick={() => setDecimals((prev) => !prev)}
        >
          <i />
        </button>
      </div>

      {unavailable ? <UnavailableFeatureDialog feature={unavailable} onClose={() => setUnavailable("")} /> : null}
    </div>
  );
}
